import React, { useEffect, useState } from "react";
import { X, Calendar, Loader2, Mail, Phone, User as UserIcon } from "lucide-react";
import { User } from "./UserDetails";

interface UpdateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: Partial<User>) => void;
  user: User | null;
  loading: boolean;
}

const buildFormData = (user: User | null) => ({
  first_name: user?.first_name || "",
  last_name: user?.last_name || "",
  email: user?.email || "",
  phonenumber: user?.phonenumber || "",
  interact_email: user?.interact_email || "",
  birthdate: user?.birthdate || "",
  expired_document_date: user?.expired_document_date || "",
  status: user?.status || "ACTIVE",
  role: user?.role || "USER",
  connexion_type: user?.connexion_type || "EMAIL",
  is_verified: user?.is_verified || "NO",
});

export const UpdateUserModal: React.FC<UpdateUserModalProps> = ({ isOpen, onClose, onConfirm, user, loading }) => {
  const [formData, setFormData] = useState(() => buildFormData(user));

  useEffect(() => {
    if (isOpen && user) {
      setFormData(buildFormData(user));
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    onConfirm(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Modifier l'utilisateur</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Informations personnelles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  Prénom
                </div>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Prénom"
              />
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  Nom
                </div>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Nom"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </div>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="email@example.com"
              />
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Téléphone
                </div>
              </label>
              <input
                type="tel"
                name="phonenumber"
                value={formData.phonenumber}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="+33 6 12 34 56 78"
              />
            </div>
          </div>

          {/* Email d'interaction */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Interac
              </div>
            </label>
            <input
              type="email"
              name="interact_email"
              value={formData.interact_email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="courriel@exemple.com"
            />
            <p className="mt-1 text-xs text-gray-500">
              Adresse depuis laquelle l&apos;utilisateur envoie ses virements Interac.
            </p>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date de naissance
                </div>
              </label>
              <input
                type="date"
                name="birthdate"
                value={formData.birthdate?.split("T")[0] || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Actuelle: {user.birthdate ? new Date(user.birthdate).toLocaleDateString("fr-FR") : "Non renseignée"}
              </p>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date d'expiration
                </div>
              </label>
              <input
                type="date"
                name="expired_document_date"
                value={formData.expired_document_date?.split("T")[0] || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Actuelle:{" "}
                {user.expired_document_date
                  ? new Date(user.expired_document_date).toLocaleDateString("fr-FR")
                  : "Non renseignée"}
              </p>
            </div>
          </div>

          {/* Statuts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Statut</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="ACTIVE">Actif</option>
                <option value="INACTIVE">Inactif</option>
                <option value="SUSPENDED">Suspendu</option>
                <option value="BANNED">Banni</option>
              </select>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Rôle</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="USER">Utilisateur</option>
                <option value="ADMIN">Administrateur</option>
                <option value="MODERATOR">Modérateur</option>
                <option value="SUPER_ADMIN">Super Admin</option>
              </select>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">Type de connexion</label>
              <select
                name="connexion_type"
                value={formData.connexion_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="EMAIL">Email</option>
                <option value="GOOGLE">Google</option>
                <option value="FACEBOOK">Facebook</option>
                <option value="APPLE">Apple</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Vérifié</label>
            <select
              name="is_verified"
              value={formData.is_verified as any}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="NO">Non</option>
              <option value="PENDING">En attente (PENDING)</option>
              <option value="REJECTED">Rejeté (REJECTED)</option>
              <option value="YES">Oui (approuvé)</option>
            </select>
          </div>

          {/* Boutons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
